import React, { useState, useEffect } from 'react';
import { useFlightFilters } from '../../contexts/FlightFilterContext';

const FlightFilters: React.FC = () => {
  const { applyFilters } = useFlightFilters();

  const getInitialState = (key: string, defaultValue: any) => {
    const saved = sessionStorage.getItem(`flights_filter_ui_${key}`);
    try { return saved !== null ? JSON.parse(saved) : defaultValue; }
    catch { return defaultValue; }
  };

  const [dateRange, setDateRange] = useState(() => getInitialState('dateRange', 'Letzte 30 Tage'));
  const [showCustomDate, setShowCustomDate] = useState(() => getInitialState('showCustomDate', false));
  const [startDate, setStartDate] = useState(() => getInitialState('startDate', ''));
  const [endDate, setEndDate] = useState(() => getInitialState('endDate', ''));
  const [aircraftType, setAircraftType] = useState(() => getInitialState('aircraftType', 'Alle Typen'));
  const [visibility, setVisibility] = useState(() => getInitialState('visibility', 'Alle'));

  useEffect(() => {
    sessionStorage.setItem('flights_filter_ui_dateRange', JSON.stringify(dateRange));
    sessionStorage.setItem('flights_filter_ui_showCustomDate', JSON.stringify(showCustomDate));
    sessionStorage.setItem('flights_filter_ui_startDate', JSON.stringify(startDate));
    sessionStorage.setItem('flights_filter_ui_endDate', JSON.stringify(endDate));
    sessionStorage.setItem('flights_filter_ui_aircraftType', JSON.stringify(aircraftType));
    sessionStorage.setItem('flights_filter_ui_visibility', JSON.stringify(visibility));
  }, [dateRange, showCustomDate, startDate, endDate, aircraftType, visibility]);

  // Calculate date range based on preset
  const calculateDateRange = (range: string): { from: string | null; to: string | null } => {
    const now = new Date();
    // Get today in local timezone as YYYY-MM-DD
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    switch (range) {
      case 'Letzte 7 Tage':
        const week = new Date(now);
        week.setDate(week.getDate() - 7);
        const weekStr = `${week.getFullYear()}-${String(week.getMonth() + 1).padStart(2, '0')}-${String(week.getDate()).padStart(2, '0')}`;
        return { from: weekStr, to: todayStr };

      case 'Letzte 30 Tage':
        const month = new Date(now);
        month.setDate(month.getDate() - 30);
        const monthStr = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}-${String(month.getDate()).padStart(2, '0')}`;
        return { from: monthStr, to: todayStr };

      case 'Letzte 90 Tage':
        const threeMonths = new Date(now);
        threeMonths.setDate(threeMonths.getDate() - 90);
        const threeMonthsStr = `${threeMonths.getFullYear()}-${String(threeMonths.getMonth() + 1).padStart(2, '0')}-${String(threeMonths.getDate()).padStart(2, '0')}`;
        return { from: threeMonthsStr, to: todayStr };

      case 'Dieses Jahr':
        const yearStartStr = `${now.getFullYear()}-01-01`;
        return { from: yearStartStr, to: todayStr };

      default:
        return { from: null, to: null };
    }
  };

  const handleDateRangeSelect = (range: string) => {
    if (range === 'Benutzerdefiniert') {
      setShowCustomDate(true);
    } else {
      setShowCustomDate(false);
      setDateRange(range);
      // Apply filters immediately
      const dates = calculateDateRange(range);
      applyFilters({
        dateFrom: dates.from,
        dateTo: dates.to,
        aircraftType: mapAircraftTypeToApi(aircraftType),
        visibility: mapVisibilityToApi(visibility)
      });
    }
  };

  const applyCustomDateRange = () => {
    if (startDate && endDate) {
      setDateRange(`${startDate} - ${endDate}`);
      setShowCustomDate(false);
      // Apply filters immediately
      applyFilters({
        dateFrom: startDate,
        dateTo: endDate,
        aircraftType: mapAircraftTypeToApi(aircraftType),
        visibility: mapVisibilityToApi(visibility)
      });
    }
  };

  const mapAircraftTypeToApi = (displayType: string): string => {
    const mapping: { [key: string]: string } = {
      'Alle Typen': 'all',
      'Ballon': 'balloon',
      'Segelflugzeug': 'glider',
      'Motorflugzeug': 'plane',
      'Hubschrauber': 'helicopter'
    };
    return mapping[displayType] || 'all';
  };

  const mapVisibilityToApi = (displayVisibility: string): string => {
    const mapping: { [key: string]: string } = {
      'Alle': 'all',
      'Öffentlich': 'public',
      'Privat': 'private'
    };
    return mapping[displayVisibility] || 'all';
  };

  const handleApplyFilters = () => {
    const dates = dateRange.includes(' - ')
      ? { from: startDate, to: endDate }
      : calculateDateRange(dateRange);

    applyFilters({
      dateFrom: dates.from,
      dateTo: dates.to,
      aircraftType: mapAircraftTypeToApi(aircraftType),
      visibility: mapVisibilityToApi(visibility)
    });
  };

  // Apply default filter on mount
  useEffect(() => {
    handleApplyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="card shadow-sm mb-4" style={{ borderRadius: '12px' }}>
      <div className="card-body p-4">
        <div className="d-flex flex-wrap gap-3 align-items-center">
          {/* Zeitraum Filter */}
          <span style={{ color: '#687088', fontSize: '14px' }}>Zeitraum</span>
          <div className="dropdown">
            <button
              className="btn btn-primary dropdown-toggle"
              style={{ fontWeight: 700, fontSize: '13px' }}
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {dateRange}
            </button>
            <ul className="dropdown-menu" style={{ minWidth: showCustomDate ? '320px' : 'auto', padding: showCustomDate ? '16px' : '' }} onClick={(e) => { if (showCustomDate) e.stopPropagation(); }}>
              {!showCustomDate ? (
                <>
                  <li><a className={`dropdown-item ${dateRange === 'Letzte 7 Tage' ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); handleDateRangeSelect('Letzte 7 Tage'); }}>Letzte 7 Tage</a></li>
                  <li><a className={`dropdown-item ${dateRange === 'Letzte 30 Tage' ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); handleDateRangeSelect('Letzte 30 Tage'); }}>Letzte 30 Tage</a></li>
                  <li><a className={`dropdown-item ${dateRange === 'Letzte 90 Tage' ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); handleDateRangeSelect('Letzte 90 Tage'); }}>Letzte 90 Tage</a></li>
                  <li><a className={`dropdown-item ${dateRange === 'Dieses Jahr' ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); handleDateRangeSelect('Dieses Jahr'); }}>Dieses Jahr</a></li>
                  <li><hr className="dropdown-divider" /></li>
                  <li><a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDateRangeSelect('Benutzerdefiniert'); }}>Benutzerdefiniert...</a></li>
                </>
              ) : (
                <div onClick={(e) => e.stopPropagation()}>
                  <div className="mb-3">
                    <label className="form-label" style={{ fontSize: '13px', color: '#687088', fontWeight: 600 }}>Von</label>
                    <input
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      style={{ fontSize: '13px' }}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" style={{ fontSize: '13px', color: '#687088', fontWeight: 600 }}>Bis</label>
                    <input
                      type="date"
                      className="form-control"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      style={{ fontSize: '13px' }}
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      style={{ fontSize: '13px', flex: 1 }}
                      onClick={() => { setShowCustomDate(false); setStartDate(''); setEndDate(''); }}
                    >
                      Abbrechen
                    </button>
                    <button
                      className="btn btn-sm btn-primary"
                      style={{ fontSize: '13px', fontWeight: 700, color: 'white', flex: 1 }}
                      onClick={applyCustomDateRange}
                      disabled={!startDate || !endDate}
                    >
                      Anwenden
                    </button>
                  </div>
                </div>
              )}
            </ul>
          </div>

          {/* Flugobjekt Filter */}
          <span style={{ color: '#687088', fontSize: '14px' }}>Flugobjekt</span>
          <div className="dropdown">
            <button
              className="btn btn-outline-secondary dropdown-toggle"
              style={{ color: '#687088', borderColor: '#dee2e6', fontSize: '13px' }}
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {aircraftType}
            </button>
            <ul className="dropdown-menu">
              <li><a className={`dropdown-item ${aircraftType === 'Alle Typen' ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); const type = 'Alle Typen'; setAircraftType(type); const dates = dateRange.includes(' - ') ? { from: startDate, to: endDate } : calculateDateRange(dateRange); applyFilters({ dateFrom: dates.from, dateTo: dates.to, aircraftType: mapAircraftTypeToApi(type), visibility: mapVisibilityToApi(visibility) }); }}>Alle Typen</a></li>
              <li><a className={`dropdown-item ${aircraftType === 'Ballon' ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); const type = 'Ballon'; setAircraftType(type); const dates = dateRange.includes(' - ') ? { from: startDate, to: endDate } : calculateDateRange(dateRange); applyFilters({ dateFrom: dates.from, dateTo: dates.to, aircraftType: mapAircraftTypeToApi(type), visibility: mapVisibilityToApi(visibility) }); }}>Ballon</a></li>
              <li><a className={`dropdown-item ${aircraftType === 'Segelflugzeug' ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); const type = 'Segelflugzeug'; setAircraftType(type); const dates = dateRange.includes(' - ') ? { from: startDate, to: endDate } : calculateDateRange(dateRange); applyFilters({ dateFrom: dates.from, dateTo: dates.to, aircraftType: mapAircraftTypeToApi(type), visibility: mapVisibilityToApi(visibility) }); }}>Segelflugzeug</a></li>
              <li><a className={`dropdown-item ${aircraftType === 'Motorflugzeug' ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); const type = 'Motorflugzeug'; setAircraftType(type); const dates = dateRange.includes(' - ') ? { from: startDate, to: endDate } : calculateDateRange(dateRange); applyFilters({ dateFrom: dates.from, dateTo: dates.to, aircraftType: mapAircraftTypeToApi(type), visibility: mapVisibilityToApi(visibility) }); }}>Motorflugzeug</a></li>
              <li><a className={`dropdown-item ${aircraftType === 'Hubschrauber' ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); const type = 'Hubschrauber'; setAircraftType(type); const dates = dateRange.includes(' - ') ? { from: startDate, to: endDate } : calculateDateRange(dateRange); applyFilters({ dateFrom: dates.from, dateTo: dates.to, aircraftType: mapAircraftTypeToApi(type), visibility: mapVisibilityToApi(visibility) }); }}>Hubschrauber</a></li>
            </ul>
          </div>

          {/* Sichtbarkeit Filter */}
          <span style={{ color: '#687088', fontSize: '14px' }}>Sichtbarkeit</span>
          <div className="dropdown">
            <button
              className="btn btn-outline-secondary dropdown-toggle"
              style={{ color: '#687088', borderColor: '#dee2e6', fontSize: '13px' }}
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {visibility}
            </button>
            <ul className="dropdown-menu">
              <li><a className={`dropdown-item ${visibility === 'Alle' ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); const vis = 'Alle'; setVisibility(vis); const dates = dateRange.includes(' - ') ? { from: startDate, to: endDate } : calculateDateRange(dateRange); applyFilters({ dateFrom: dates.from, dateTo: dates.to, aircraftType: mapAircraftTypeToApi(aircraftType), visibility: mapVisibilityToApi(vis) }); }}>Alle</a></li>
              <li><a className={`dropdown-item ${visibility === 'Öffentlich' ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); const vis = 'Öffentlich'; setVisibility(vis); const dates = dateRange.includes(' - ') ? { from: startDate, to: endDate } : calculateDateRange(dateRange); applyFilters({ dateFrom: dates.from, dateTo: dates.to, aircraftType: mapAircraftTypeToApi(aircraftType), visibility: mapVisibilityToApi(vis) }); }}>Öffentlich</a></li>
              <li><a className={`dropdown-item ${visibility === 'Privat' ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); const vis = 'Privat'; setVisibility(vis); const dates = dateRange.includes(' - ') ? { from: startDate, to: endDate } : calculateDateRange(dateRange); applyFilters({ dateFrom: dates.from, dateTo: dates.to, aircraftType: mapAircraftTypeToApi(aircraftType), visibility: mapVisibilityToApi(vis) }); }}>Privat</a></li>
            </ul>
          </div>
          
          <button 
            className="btn btn-outline-secondary ms-auto"
            style={{ fontSize: '13px' }}
            onClick={() => {
              setDateRange('Letzte 30 Tage');
              setAircraftType('Alle Typen');
              setVisibility('Alle');
              setShowCustomDate(false);
              setStartDate('');
              setEndDate('');
              const dates = calculateDateRange('Letzte 30 Tage');
              applyFilters({
                dateFrom: dates.from,
                dateTo: dates.to,
                aircraftType: 'all',
                visibility: 'all'
              });
            }}
          >
            Zurücksetzen
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlightFilters;
